import { EntityTypes, SortOrder, Timestamps, Versioned } from './shared.types';
import { InventoryValuation } from './products.types';
import { Supplier } from './suppliers.types';

export enum InventoryChangeReason {
  SALE = 'Sale',
  REFUND = 'Refund',
  PURCHASE_ORDER = 'Purchase Order',
  INVENTORY_TRANSFER_IN = 'Inventory Transfer In',
  INVENTORY_TRANSFER_OUT = 'Inventory Transfer Out',
  INVENTORY_COUNT = 'Inventory Count',
  INVENTORY_CORRECTION = 'Inventory Correction',
}

/**
 * Where stock sits (docs/plans/orders-and-transfers.md §4.3). AVAILABLE is
 * the approved on-hand outside reservations; NONE is outside the store (a
 * correction that adds or writes off stock).
 */
export enum StockBucket {
  AVAILABLE = 'AVAILABLE',
  RESERVED = 'RESERVED',
  PENDING = 'PENDING',
  BLOCKED = 'BLOCKED',
  NONE = 'NONE',
}

/**
 * The stock operations of `POST /inventory/{merchant_id}/operations`, the
 * only writer of the bucket counters. More types (returns, purchase
 * receive) arrive with their first caller.
 */
export enum StockOperationType {
  /** AVAILABLE → RESERVED, refused beyond the sellable stock. */
  RESERVE = 'RESERVE',
  /** RESERVED → AVAILABLE. */
  RELEASE = 'RELEASE',
  /** Between AVAILABLE, PENDING and BLOCKED. */
  BUCKET_MOVE = 'BUCKET_MOVE',
  /** Into a bucket from NONE, or out of one to NONE. */
  CORRECTION = 'CORRECTION',
  /**
   * A transfer leaves its source: `quantity` comes out of the approved
   * on-hand and the line's own reservation (`reserved_quantity`) is freed
   * in the same operation, so a shortfall's reservation is released too.
   * Refused beyond `cumulative − (reserved − own reservation)`.
   */
  TRANSFER_DISPATCH = 'TRANSFER_DISPATCH',
  /**
   * A transfer arrives: `quantity` goes into `to_bucket` (AVAILABLE,
   * PENDING or BLOCKED) at the carried `unit_cost`; into AVAILABLE the
   * store's cost is re-weighted with it.
   */
  TRANSFER_RECEIVE = 'TRANSFER_RECEIVE',
  /**
   * Goods of a transfer come back to their source (PR 7): like a receipt,
   * into AVAILABLE (good, re-weighted at the carried `unit_cost`) or
   * BLOCKED (damaged).
   */
  RETURN_RECEIVE = 'RETURN_RECEIVE',
}

/** What a stock operation belongs to; ids only. */
export interface StockOperationReference {
  stock_order_id?: string;
  transfer_id?: string;
  line_id?: string;
  purchase_order_id?: string;
  goods_receipt_id?: string;
}

export interface ApplyStockOperation {
  /**
   * Deterministic id (e.g. `transfer:TR-1:line-1:reserve`): the operation
   * is applied once; a replay returns the recorded result.
   */
  operation_id: string;
  store_id: string;
  product_id: string;
  type: StockOperationType;
  /** Parent units, > 0. */
  quantity: number;
  /** Required for BUCKET_MOVE and CORRECTION. */
  from_bucket?: StockBucket;
  to_bucket?: StockBucket;
  reference?: StockOperationReference;
  /** Free-text reason, kept on the operation record. */
  note?: string;
  /** When it happened at its source; missing = now. */
  occurred_at?: string;
  /** Product name for the stock row and ledger, when known. */
  product_name?: string;
  /**
   * TRANSFER_DISPATCH only: the line's own reservation, freed by the
   * dispatch (may exceed `quantity`: the shortfall is released). Missing = 0.
   */
  reserved_quantity?: number;
  /**
   * TRANSFER_RECEIVE and RETURN_RECEIVE: the cost per unit carried from the
   * source. A BUCKET_MOVE PENDING → AVAILABLE (price approval, PR 7) with a
   * `unit_cost` re-weights the store's cost with it, as a receipt does.
   */
  unit_cost?: number;
  /**
   * Price approval only: the cost the goods were carried at. When it
   * differs from `unit_cost` (the owner's), the ledger row records the
   * valuation adjustment. Missing = no adjustment.
   */
  carried_unit_cost?: number;
}

/** The buckets after an operation, as recorded on its marker. */
export interface StockOperationCounters {
  cumulative_quantity: number;
  reserved_quantity: number;
  pending_quantity: number;
  blocked_quantity: number;
  /** Optional for backwards compatibility (records before PR 4c); missing = 0. */
  manual_reserved_quantity?: number;
}

/**
 * A stock operation as recorded by service-inventory: its replay marker and
 * the audit of a reservation (reservations write no ledger row).
 */
export interface StockOperationRecord {
  operation_id: string;
  merchant_id: string;
  store_id: string;
  product_id: string;
  type: StockOperationType;
  quantity: number;
  from_bucket?: StockBucket;
  to_bucket?: StockBucket;
  reference?: StockOperationReference;
  note?: string;
  /** Reserved or released by hand (Products app). Missing = false. */
  manual?: boolean;
  /**
   * A manual release that could free any reservation: first the stock
   * reserved by hand, then the rest. Missing = false.
   */
  release_any?: boolean;
  /** TRANSFER_DISPATCH: the own reservation it was asked to free. */
  reserved_quantity?: number;
  /**
   * A transfer's RELEASE or TRANSFER_DISPATCH: the reservation actually
   * freed, when part of it had already been released from Products
   * (release_any). Missing = what was asked.
   */
  freed_quantity?: number;
  /**
   * The cost per unit the operation moved stock at: the source's cost for
   * TRANSFER_DISPATCH, the carried cost for TRANSFER_RECEIVE.
   */
  unit_cost?: number;
  /**
   * TRANSFER_RECEIVE into AVAILABLE: the store's new cost, computed in the
   * stock transaction. A replay pushes it again (the store product skips
   * a valuation it already has).
   */
  valuation?: InventoryValuation;
  /** Identifies the request: a replay with another payload is refused. */
  fingerprint: string;
  /** The counters right after it was applied. */
  counters: StockOperationCounters;
  actor_id: string;
  actor_name: string;
  occurred_at: string;
  applied_at: string;
}

/**
 * `POST /inventory/{merchant_id}/stores/{store_id}/products/{product_id}/reservations`:
 * reserve or release stock by hand (Products app). Needs the
 * `orders:stock.reserve` permission in the store and an `Idempotency-Key`
 * header (the operation's id). RESERVE needs the Orders & Transfers module
 * ENABLED; RELEASE works unless it is FROZEN, so held stock never gets stuck.
 */
export interface ManualStockReservation {
  /** RESERVE or RELEASE. */
  type: StockOperationType;
  /** Parent units, > 0. */
  quantity: number;
  /** Why, kept on the operation record. */
  note?: string;
  /**
   * RELEASE only: free any reserved stock, the stock reserved by hand
   * first, then the rest (needs `orders:stock.release_any`).
   * Optional for backwards compatibility; missing = false (only stock
   * reserved by hand).
   */
  release_any?: boolean;
}

export interface StockOperationResult {
  operation_id: string;
  /** False when the operation had been applied before (a replay). */
  applied: boolean;
  /** The counters right after the operation was first applied. */
  counters: StockOperationCounters;
  /** As recorded (TRANSFER_DISPATCH: the source's cost per unit). */
  unit_cost?: number;
}

/** What the quantity of a count (COUNT / CORRECTION) means. */
export enum CountBasis {
  /** Everything counted on the shelf, held stock included. */
  ON_HAND = 'ON_HAND',
  /** The stock to sell (a CSV import): held stock stays where it is. */
  SELLABLE = 'SELLABLE',
}

export enum ReasonCounted {
  SALE = 'Sale',
  REFUND = 'Refund',
  PURCHASE = 'Purchase Order',
  INVENTORY_COUNT = 'Inventory Count',
  INVENTORY_CORRECTION = 'Inventory Correction',
}

export enum InventoryChangeEvent {
  INVENTORY_CHANGE_QUANTITY_UPDATED = 'INVENTORY_CHANGE_QUANTITY_UPDATED',
  INVENTORY_CUMULATIVE_QUANTITY_UPDATED = 'INVENTORY_CUMULATIVE_QUANTITY_UPDATED',
  INVENTORY_CHANGE_REASON_UPDATED = 'INVENTORY_CHANGE_REASON_UPDATED',
  INVENTORY_CHANGE_SOURCE_UPDATED = 'INVENTORY_CHANGE_SOURCE_UPDATED',
  INVENTORY_CHANGE_DELETED_AT_UPDATED = 'INVENTORY_CHANGE_DELETED_AT_UPDATED',
  INVENTORY_CHANGE_SOURCE_ID_UPDATED = 'INVENTORY_CHANGE_SOURCE_ID_UPDATED',
  INVENTORY_CHANGE_SOURCE_TYPE_UPDATED = 'INVENTORY_CHANGE_SOURCE_TYPE_UPDATED',
}

export interface FractionCountItem {
  id: string;
  unit: string;
  fraction: number;
  expectedStock: number;
  qty_in_package: number;
  is_sold_by_weight: boolean;
  unit_of_measurement: string;
}

export interface PurchaseOrderDetails {
  id: string;
  supplier: Supplier;
}

export interface InventoryChange extends Timestamps {
  id: string; // Unique change_id
  store_id: string;
  product_id: string;
  product_name: string;
  change_quantity: number;
  cumulative_quantity: number;
  reason: InventoryChangeReason;
  merchant_id: string;
  created_at: string; // ISO 8601 date string. Represents the time the change actually occurred (e.g. sale/refund time at POS), NOT necessarily the time it was persisted.
  type: 'CHANGE';
  // Additional attributes
  source_id: string;
  source_type: EntityTypes;
  cost: number;
  price: number;
  source_name: string;
  purchase_order_details?: PurchaseOrderDetails;
  transfer_store_id?: string;
  inventory_value_change: number;
  retail_value_change: number;
  change_fraction: number;
  // Optional ISO 8601 timestamp recording when the inventory change record was actually written/processed.
  // Useful for offline-sync scenarios where created_at reflects the original sale time and processed_at reflects ingest time.
  processed_at?: string;
  /**
   * Set on rows written by a stock operation: the bucket the quantity moved
   * into (or out of, for a negative change). Optional for backwards
   * compatibility; missing = AVAILABLE (every older row).
   */
  bucket?: StockBucket;
  /** The operation type behind the row (e.g. `BUCKET_MOVE`). Optional; missing on older rows. */
  sub_reason?: string;
  /** What the operation belongs to. Optional; missing on older rows. */
  reference?: StockOperationReference;
  /** The stock operation that wrote the row. Optional; missing on older rows. */
  operation_id?: string;
  /**
   * Price approval with an owner cost (PR 7): (approved − carried cost) ×
   * quantity, the explicit valuation adjustment reports show on its own
   * line. Optional; missing = none.
   */
  valuation_adjustment?: number;
  /**
   * Free text from the writer, e.g. `POS Caixa 1, OFFLINE` for a count made
   * on a till. Optional for backwards compatibility; missing = no note.
   */
  note?: string;
}

export interface CreateInventoryChange {
  store_id: string;
  product_id: string;
  change_quantity: number;
  reason: InventoryChangeReason;
  merchant_id: string;
  source_id: string;
  cost: number;
  source_type: EntityTypes;
  source_name: string;
  product_name: string;
  purchase_order_details?: PurchaseOrderDetails;
  price: number;
  transfer_store_id?: string;
  inventory_value_change: number;
  retail_value_change: number;
  change_fraction: number;
  // ISO 8601 timestamp of when the change actually occurred at its source (e.g. receipt.created_at on the POS device).
  // This becomes the persisted created_at on the InventoryChange record so date-keyed indexes reflect business-occurrence time,
  // not arrival/processing time. Required so offline-synced sales/refunds land on the correct day.
  source_created_at: string;
  /**
   * Makes a redelivered change a no-op: the first change with this key is
   * applied, later ones return the current stock unchanged. Receipts send
   * `<receipt_number>#<line id>`. Optional for backwards compatibility;
   * missing = every delivery is applied (legacy behaviour).
   */
  idempotency_key?: string;
  /**
   * For COUNT / CORRECTION: what `change_quantity` means. The server turns
   * it into `cumulative_quantity` from the stock it reads (and re-reads on
   * a conflict), so a SELLABLE target never goes stale against a
   * reservation that changed meanwhile. Optional for backwards
   * compatibility; missing = ON_HAND.
   */
  count_basis?: CountBasis;
  /** Stored on the change as `note`. Optional; missing = no note. */
  note?: string;
  /**
   * For COUNT / CORRECTION: the time the stock was counted (ISO 8601). The
   * count is taken as true at that time: the movements that occurred after
   * it (sales, receipts, transfers) are added on top, so a count uploaded
   * late never erases them. A count or correction recorded after this time
   * wins and the request is refused with 409. Needs `idempotency_key`; at
   * most 35 days old, not in the future; the change is dated at this time.
   * Optional for backwards compatibility; missing = the count replaces the
   * stock as it is now.
   */
  count_as_of?: string;
}

export interface CurrentInventory extends Timestamps, Versioned {
  id: string;
  store_id: string;
  product_id: string;
  cumulative_quantity: number;
  merchant_id: string;
  type: 'CURRENT';
  cost: number;
  price: number;
  inventory_value_change: number;
  retail_value_change: number;
  change_fraction: number;
  change_quantity: number;
  /** ISO 8601 timestamp of the last write/processing of this record. */
  processed_at?: string;
  /** Product name at the latest change, for the audit trail. Optional; missing on older rows. */
  product_name?: string | null;
  /**
   * Authorized for a transfer, not yet dispatched. Part of
   * `cumulative_quantity` (sellable = cumulative − reserved).
   * Optional for backwards compatibility; missing = 0.
   */
  reserved_quantity?: number;
  /**
   * The part of `reserved_quantity` set aside by hand (Products app); only a
   * manual release frees it, and a transfer's release never does.
   * Optional for backwards compatibility; missing = 0.
   */
  manual_reserved_quantity?: number;
  /**
   * Received, waiting for price approval. Not in `cumulative_quantity`.
   * Optional for backwards compatibility; missing = 0.
   */
  pending_quantity?: number;
  /**
   * Extra goods awaiting validation and damaged returns. Not in
   * `cumulative_quantity`. Optional for backwards compatibility; missing = 0.
   */
  blocked_quantity?: number;
  /**
   * The cost per unit the last transfer receipt valued the stock at, inside
   * the stock transaction (so receipts are weighted in commit order) and
   * pushed to the store product. Optional; missing = the store product's
   * cost is the base of the next valuation.
   */
  valuation_cost?: number;
  /** When `valuation_cost` was computed. Optional; set with it. */
  valuation_at?: string;
  /**
   * The store product's `cost_changed_at` that `valuation_cost` started
   * from (null: none). While the store product still has it, the
   * valuation is the store's cost even before products has heard of it;
   * once it differs (a hand edit, a purchase-order receive), the store
   * product's cost is. Optional; set with `valuation_cost`.
   */
  valuation_base_changed_at?: string | null;
}

/**
 * Where a store's stock of a product sits, in parent units
 * (docs/plans/orders-and-transfers.md §4.3). `on_hand` is what a count
 * finds: cumulative + pending + blocked. Sellable stock is the store
 * product's `inventory`.
 */
export interface StockBreakdown {
  on_hand: number;
  reserved: number;
  pending: number;
  blocked: number;
  /**
   * The part of `reserved` set aside by hand, which a manual release may
   * free. Optional for backwards compatibility; missing = 0.
   */
  manual_reserved?: number | null;
}

export interface InventoryChangeDBItem extends InventoryChange {
  // PK: `STORE#${store_id}#PRODUCT#${product_id}`
  // SK: `INVENTORY_CHANGE#DATE#${created_at}#INVENTORY_CHANGE#${id}`
  PK: string;
  SK: string;

  // GSI1PK: `PRODUCT#${product_id}`
  // GSI1SK: `INVENTORY_CHANGE#STORE#${store_id}#DATE#${created_at}`
  GSI1PK: string;
  GSI1SK: string;

  // GSI2PK: `REASON#${reason}#STORE#${store_id}`
  // GSI2SK: `INVENTORY_CHANGE#PRODUCT#${product_id}#DATE#${created_at}`
  GSI2PK: string;
  GSI2SK: string;

  // GSI3PK: `SOURCE#${source}#STORE#${store_id}`
  // GSI3SK: `INVENTORY_CHANGE#PRODUCT#${product_id}#DATE#${created_at}`
  GSI3PK: string;
  GSI3SK: string;

  // GSI4PK: `REASON#${reason}#SOURCE#${source}#STORE#${store_id}`
  // GSI4SK: `INVENTORY_CHANGE#PRODUCT#${product_id}#DATE#${created_at}`
  GSI4PK: string;
  GSI4SK: string;

  // GSI5PK: `REASON#${reason}`
  // GSI5SK: `INVENTORY_CHANGE#PRODUCT#${product_id}#STORE#${store_id}#DATE#${created_at}`
  GSI5PK: string;
  GSI5SK: string;

  // GSI6PK: `SOURCE#${source}`
  // GSI6SK: `INVENTORY_CHANGE#PRODUCT#${product_id}#STORE#${store_id}#DATE#${created_at}`
  GSI6PK: string;
  GSI6SK: string;

  // GSI7PK: `REASON#${reason}#SOURCE#${source}`
  // GSI7SK: `INVENTORY_CHANGE#PRODUCT#${product_id}#STORE#${store_id}#DATE#${created_at}`
  GSI7PK: string;
  GSI7SK: string;

  // EntityIndexPK: `INVENTORY_CHANGE`
  // EntityIndexSK: `MERCHANT#${merchant_id}#STORE#${store_id}#PRODUCT#${product_id}#DATE#${created_at}`
  EntityIndexPK: string;
  EntityIndexSK: string;

  /**
   * Processed-time index (when the change was applied, not when it
   * happened). Optional for backwards compatibility: missing on rows written
   * before it existed until the repair backfills them.
   */
  // GSI8PK: `INVENTORY_CHANGE#MERCHANT#${merchant_id}#STORE#${store_id}`
  // GSI8SK: `PROCESSED#${processed_at}#INVENTORY_CHANGE#${id}`
  GSI8PK?: string;
  GSI8SK?: string;
}

export interface CurrentInventoryDBItem extends CurrentInventory {
  // PK: `STORE#${store_id}#PRODUCT#${product_id}`
  // SK: `TYPE#CURRENT`
  PK: string;
  SK: string;

  // GSI1PK: `PRODUCT#${product_id}`
  // GSI1SK: `TYPE#CURRENT#STORE#${store_id}`
  GSI1PK: string;
  GSI1SK: string;

  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface GetInventoryChanges {
  /**
   * Flags to determine which query to perform.
   * Only one should be true per request.
   */
  by_store_and_product_and_date_range?: boolean;
  by_store_and_date_range?: boolean;
  by_reason_and_store_and_product_and_date_range?: boolean;
  by_reason_and_store?: boolean;
  by_reason_across_stores?: boolean;
  by_source_and_store?: boolean;
  by_source_across_stores?: boolean;
  by_reason_and_source_and_store?: boolean;
  by_reason_and_source_across_stores?: boolean;
  by_product_and_store?: boolean;
  by_product_across_stores?: boolean;
  by_reason_and_product_and_store?: boolean;
  by_reason_and_product_across_stores?: boolean;
  by_source_and_product_and_store?: boolean;
  by_source_and_product_across_stores?: boolean;
  by_source_and_reason_and_product?: boolean;
  by_source_and_reason_and_store?: boolean;
  by_source_and_reason_across_stores?: boolean;
  /**
   * Common query parameters.
   */
  limit?: number;
  cursor?: string;
  sort_order?: SortOrder;

  /**
   * Filter parameters.
   */
  store_id?: string;
  product_id?: string;
  reason?: InventoryChangeReason;
  source_type?: EntityTypes;
  source_id?: string;
  merchant_id: string; // For merchant-level queries

  start_date?: string; // ISO 8601 date string
  end_date?: string; // ISO 8601 date string
}

export interface InventoryChangePage {
  changes: InventoryChange[];
  cursor?: string;
  count: number;
}

/**
 * GET {merchant_id}/stores/{store_id}/changes/applied-late: changes that
 * happened before `start_date` but were applied (processed) after
 * `applied_after` and up to `applied_until`, e.g. a sale synced late by an
 * offline POS. The valuation report passes its opening and closing value
 * snapshots' times: a snapshot holds the changes applied before it was
 * taken, so these are in the closing value but not in the period's changes.
 */
export interface GetInventoryChangesAppliedLate {
  /** Period start (ISO 8601): only changes that happened before it. */
  start_date: string;
  /** Applied after this time (ISO 8601, exclusive): the opening snapshot's. */
  applied_after: string;
  /** Applied up to this time (ISO 8601, inclusive): the closing snapshot's. */
  applied_until: string;
}

export interface InventoryChangesAppliedLate {
  store_id: string;
  start_date: string;
  applied_after: string;
  applied_until: string;
  /** Number of changes. */
  count: number;
  /** Sum of their signed inventory (cost) value changes. */
  inventory_value_change: number;
  /** Sum of their signed retail value changes. */
  retail_value_change: number;
}

/**
 * POST {merchant_id}/stores/{store_id}/changes/repair-processed-index:
 * adds the processed-time index to one page of the store's changes written
 * before it existed. Idempotent and resumable; dry run unless
 * `dry_run` is false.
 */
export interface RepairInventoryChangesProcessedIndex {
  cursor?: string;
  /** Changes read per call (default 500). */
  limit?: number;
  /** Optional for backwards compatibility; missing = true (nothing written). */
  dry_run?: boolean;
}

export interface RepairInventoryChangesProcessedIndexResult {
  dry_run: boolean;
  /** Changes read in this page. */
  scanned: number;
  /** Changes among them without the index. */
  missing: number;
  /** Changes indexed by this call (0 in a dry run). */
  written: number;
  /** Next page, or null when the store is done. */
  cursor: string | null;
}

export interface GetStoreInventories {
  store_id: string;
  merchant_id: string;
}

export interface StoreInventoriesResponse {
  inventories: CurrentInventory[];
  count: number;
}
